import { BrowserRouter as Router, Routes, Route, Link } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { DarkModeProvider } from "./context/DarkModeContext";
import AuthLayout from "./components/layout/AuthLayout";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";

// Public Pages
import Home from "./pages/HomePage";
import About from "./pages/About";
import Packages from "./pages/Packages";
import Login from "./pages/Login";
import Signup from "./pages/Sign-up";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import ReactivateAccount from "./pages/ReactivateAccount";
import EmailVerification from "./pages/EmailVerification";
import PaymentPage from "./components/PaymentPage";
import PaymentFormWrapper from "./components/PaymentFormWrapper";
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import ContactSupport from "./pages/ContactSupport";
import FAQ from "./pages/FAQ";
import HelpCenter from "./pages/Help";
import Community from "./pages/Community";
import EventCalendar from "./pages/EventCalendar";

// Dashboard Layout + Pages
import DashboardLayout from "./components/DashboardLayout";
import Profile from "./pages/dashboard/Profile";
import Settings from "./pages/dashboard/Settings";
import FindJob from "./pages/dashboard/Findjob";
import CreateJob from "./pages/dashboard/CreateJob";
import AdminPanel from "./pages/dashboard/AdminPanel";
import EmployersPackages from "./pages/EmployersPackages";

// Auth
import ProtectedRoute from "./components/ProtectedRoute";
import ApplyJobForm from "./pages/dashboard/ApplyJobForm";
import ApplicationList from "./pages/dashboard/ApplicationList";
import AdminPackagesManager from "./pages/dashboard/AdminPackagesManager";
import WhitelistManagement from "./pages/dashboard/WhitelistManager";
import EditJob from "./pages/dashboard/Editjob";
import ContactFormsManager from "./pages/dashboard/ContactFormManager";
import UserWhitelistRequest from "./pages/dashboard/UserWhiteListRequest";
import EventsManager from "./pages/dashboard/EventManager";
import EditEvent from "./pages/dashboard/EditEvent";
import CreateEvent from "./pages/dashboard/CreateEvent";
import WhitelistApplications from "./pages/dashboard/WhitelistApplications";
import SecurityPage from "./pages/SecurityPage";

// Articles
import Guides from "./pages/articles/Guide";
import Articles from "./pages/articles/Article";
import ArticleDetail from "./pages/articles/components/ArticleDetail";
import SearchResults from "./pages/SearchResults";

// Layout wrapper component for public routes
const PublicLayout = ({ children }: { children: React.ReactNode }) => (
  <>
    <Navbar />
    {children}
    <Footer />
  </>
);

function App() {
  return (
    <AuthProvider>
      <DarkModeProvider>
        <Router>
          <div className="flex flex-col min-h-screen bg-white dark:bg-gray-900 transition-colors duration-300">
            <Routes>
              {/* ===== PUBLIC ROUTES ===== */}

              {/* Auth Routes */}
              <Route
                path="/login"
                element={
                  <AuthLayout>
                    <Login />
                  </AuthLayout>
                }
              />
              <Route
                path="/signup"
                element={
                  <AuthLayout>
                    <Signup />
                  </AuthLayout>
                }
              />
              <Route
                path="/email-verification"
                element={
                  <AuthLayout>
                    <EmailVerification />
                  </AuthLayout>
                }
              />
              <Route
                path="/forgot-password"
                element={
                  <AuthLayout>
                    <ForgotPassword />
                  </AuthLayout>
                }
              />
              <Route
                path="/reset-password"
                element={
                  <AuthLayout>
                    <ResetPassword />
                  </AuthLayout>
                }
              />
              <Route
                path="/reactivate-account"
                element={
                  <AuthLayout>
                    <ReactivateAccount />
                  </AuthLayout>
                }
              />

              {/* Public Pages with Layout */}
              <Route
                path="/"
                element={
                  <PublicLayout>
                    <Home />
                  </PublicLayout>
                }
              />
              <Route
                path="/about"
                element={
                  <PublicLayout>
                    <About />
                  </PublicLayout>
                }
              />
              <Route
                path="/packages"
                element={
                  <PublicLayout>
                    <Packages />
                  </PublicLayout>
                }
              />
              <Route
                path="/employers-packages"
                element={
                  <PublicLayout>
                    <EmployersPackages />
                  </PublicLayout>
                }
              />
              <Route
                path="/terms-of-service"
                element={
                  <PublicLayout>
                    <TermsOfService />
                  </PublicLayout>
                }
              />
              <Route
                path="/privacy-policy"
                element={
                  <PublicLayout>
                    <PrivacyPolicy />
                  </PublicLayout>
                }
              />
              <Route
                path="/contact-support"
                element={
                  <PublicLayout>
                    <ContactSupport />
                  </PublicLayout>
                }
              />
              <Route
                path="/faq"
                element={
                  <PublicLayout>
                    <FAQ />
                  </PublicLayout>
                }
              />
              <Route
                path="/help-center"
                element={
                  <PublicLayout>
                    <HelpCenter />
                  </PublicLayout>
                }
              />
              <Route
                path="/community"
                element={
                  <PublicLayout>
                    <Community />
                  </PublicLayout>
                }
              />
              <Route
                path="/event-calendar"
                element={
                  <PublicLayout>
                    <EventCalendar />
                  </PublicLayout>
                }
              />
              <Route
                path="/security"
                element={
                  <PublicLayout>
                    <SecurityPage />
                  </PublicLayout>
                }
              />
              <Route
                path="/search"
                element={
                  <PublicLayout>
                    <SearchResults />
                  </PublicLayout>
                }
              />

              {/* Help & Articles Routes */}
              <Route
                path="/help/articles"
                element={
                  <PublicLayout>
                    <Articles />
                  </PublicLayout>
                }
              />
              <Route
                path="/articles"
                element={
                  <PublicLayout>
                    <Articles />
                  </PublicLayout>
                }
              />
              <Route
                path="/articles/:articleId"
                element={
                  <PublicLayout>
                    <ArticleDetail />
                  </PublicLayout>
                }
              />
              <Route
                path="/help/articles/:articleId"
                element={
                  <PublicLayout>
                    <ArticleDetail />
                  </PublicLayout>
                }
              />
              <Route
                path="/help/articles/category/:categoryName"
                element={
                  <PublicLayout>
                    <Articles />
                  </PublicLayout>
                }
              />
              <Route
                path="/help/guides/category/:categoryName"
                element={
                  <PublicLayout>
                    <Guides />
                  </PublicLayout>
                }
              />
              <Route
                path="/help/guides/:guideId"
                element={
                  <PublicLayout>
                    <Guides />
                  </PublicLayout>
                }
              />
              <Route
                path="/guides"
                element={
                  <PublicLayout>
                    <Guides />
                  </PublicLayout>
                }
              />
              <Route
                path="/guides/:guideId"
                element={
                  <PublicLayout>
                    <Guides />
                  </PublicLayout>
                }
              />
              <Route
                path="/guides/category/:categoryName"
                element={
                  <PublicLayout>
                    <Guides />
                  </PublicLayout>
                }
              />

              {/* ===== PROTECTED ROUTES ===== */}

              {/* Payment Routes */}
              <Route
                path="/payment"
                element={
                  <ProtectedRoute
                    requireAuth={true}
                    requireSubscription={false}
                  >
                    <PublicLayout>
                      <PaymentPage />
                    </PublicLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/payment-forms"
                element={
                  <ProtectedRoute
                    requireAuth={true}
                    requireSubscription={false}
                  >
                    <PublicLayout>
                      <PaymentFormWrapper />
                    </PublicLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/checkout"
                element={
                  <ProtectedRoute
                    requireAuth={true}
                    requireSubscription={false}
                  >
                    <PublicLayout>
                      <PaymentPage />
                    </PublicLayout>
                  </ProtectedRoute>
                }
              />

              {/* Standalone Protected Routes */}
              <Route
                path="/find-job"
                element={
                  <ProtectedRoute requireSubscription={true}>
                    <PublicLayout>
                      <FindJob />
                    </PublicLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/find-job/:jobId"
                element={
                  <ProtectedRoute requireSubscription={true}>
                    <PublicLayout>
                      <FindJob />
                    </PublicLayout>
                  </ProtectedRoute>
                }
              />

              {/* ===== DASHBOARD ROUTES ===== */}
              <Route
                path="/dashboard/*"
                element={
                  <ProtectedRoute requireAuth={true}>
                    <DashboardLayout />
                  </ProtectedRoute>
                }
              >
                {/* Dashboard Index */}
                <Route
                  index
                  element={
                    <div className="p-6">
                      <h1 className="text-2xl font-bold mb-4 dark:text-white">
                        Dashboard Overview
                      </h1>
                      <p className="dark:text-gray-300">
                        Welcome to your dashboard. Use the sidebar to navigate.
                      </p>
                    </div>
                  }
                />

                {/* User Routes */}
                <Route path="profile" element={<Profile />} />
                <Route path="settings" element={<Settings />} />
                <Route path="applications" element={<ApplicationList />} />
                <Route path="packages" element={<Packages />} />
                <Route
                  path="whitelist-requests"
                  element={<UserWhitelistRequest />}
                />
                <Route path="apply/:jobId" element={<ApplyJobForm />} />
                <Route path="find-job" element={<FindJob />} />
                <Route path="find-job/:jobId" element={<FindJob />} />
                <Route path="security" element={<SecurityPage />} />

                {/* Job Creation Routes - Available for Admins and Whitelisted Users */}
                <Route
                  path="create-job"
                  element={
                    <ProtectedRoute
                      adminOnly={false}
                      requireJobPostingAccess={true}
                    >
                      <CreateJob />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="edit-job/:id"
                  element={
                    <ProtectedRoute
                      adminOnly={false}
                      requireJobPostingAccess={true}
                    >
                      <EditJob />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="applications-whitelist"
                  element={
                    <ProtectedRoute
                      adminOnly={false}
                      requireJobPostingAccess={true}
                    >
                      <WhitelistApplications />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="applications-whitelist/:applicationId"
                  element={
                    <ProtectedRoute
                      adminOnly={false}
                      requireJobPostingAccess={true}
                    >
                      <WhitelistApplications />
                    </ProtectedRoute>
                  }
                />

                {/* Admin Only Routes */}
                <Route
                  path="admin-packages"
                  element={
                    <ProtectedRoute adminOnly={true}>
                      <AdminPackagesManager />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="whitelist-management"
                  element={
                    <ProtectedRoute adminOnly={true}>
                      <WhitelistManagement />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="events-manager"
                  element={
                    <ProtectedRoute adminOnly={true}>
                      <EventsManager />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="create-event"
                  element={
                    <ProtectedRoute adminOnly={true}>
                      <CreateEvent />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="edit-event/:id"
                  element={
                    <ProtectedRoute adminOnly={true}>
                      <EditEvent />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="contact-forms"
                  element={
                    <ProtectedRoute adminOnly={true}>
                      <ContactFormsManager />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin-panel"
                  element={
                    <ProtectedRoute adminOnly={true}>
                      <AdminPanel />
                    </ProtectedRoute>
                  }
                />
              </Route>

              {/* ===== 404 NOT FOUND ===== */}
              <Route
                path="*"
                element={
                  <PublicLayout>
                    <div className="flex-1 flex items-center justify-center py-16">
                      <div className="text-center">
                        <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
                          404
                        </h1>
                        <p className="text-xl text-gray-600 dark:text-gray-400 mb-8">
                          Page not found
                        </p>
                        <Link
                          to="/"
                          className="bg-sky-600 text-white px-6 py-3 rounded-lg hover:bg-sky-700 transition-colors inline-block"
                        >
                          Go Home
                        </Link>
                      </div>
                    </div>
                  </PublicLayout>
                }
              />
            </Routes>
          </div>
        </Router>
      </DarkModeProvider>
    </AuthProvider>
  );
}

export default App;
